import React, { useMemo, useState } from 'react'
import {
  Tabs,
  TABS_DEMO_ITEMS,
  TABS_LIVE_ITEMS,
  TABS_PREVIEW_COUNT_LABELS,
  TABS_PREVIEW_COUNTS,
  type TabsOrientation,
  type TabsPreviewCount,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './TabsShowcase.css'

const ORIENTATION_LABELS: Record<TabsOrientation, string> = {
  horizontal: 'Horizontal',
  vertical: 'Vertical',
}

const TabsShowcase: React.FC = () => {
  const [orientation, setOrientation] = useState<TabsOrientation>('horizontal')
  const [activeTab, setActiveTab] = useState(TABS_LIVE_ITEMS[0].id)
  const [highlightedCount, setHighlightedCount] = useState<TabsPreviewCount>(4)
  const [darkMode, setDarkMode] = useState(false)
  const isVertical = orientation === 'vertical'

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'tabs-orientation',
            label: 'Orientation',
            control: (
              <select
                id="tabs-orientation"
                className="ui-field-control"
                value={orientation}
                onChange={(event) =>
                  setOrientation(event.target.value as TabsOrientation)
                }
              >
                {(Object.keys(ORIENTATION_LABELS) as TabsOrientation[]).map(
                  (option) => (
                    <option key={option} value={option}>
                      {ORIENTATION_LABELS[option]}
                    </option>
                  ),
                )}
              </select>
            ),
          },
          {
            id: 'tabs-highlight-count',
            label: 'Highlighted tab count',
            control: (
              <select
                id="tabs-highlight-count"
                className="ui-field-control"
                value={highlightedCount}
                onChange={(event) =>
                  setHighlightedCount(
                    Number(event.target.value) as TabsPreviewCount,
                  )
                }
              >
                {TABS_PREVIEW_COUNTS.map((count) => (
                  <option key={count} value={count}>
                    {TABS_PREVIEW_COUNT_LABELS[count]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'tabs-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="tabs-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [darkMode, highlightedCount, orientation],
  )

  const activeLabel =
    TABS_LIVE_ITEMS.find((item) => item.id === activeTab)?.label ?? 'Tab'

  return (
    <>
      <UiPreviewCanvas
        title="Tabs"
        description="Segmented horizontal tabs and stacked vertical tabs for switching related content within a page."
        stageClassName="ui-preview__stage--wide tabs-showcase__stage-wrap"
      >
        <div
          className={`tabs-showcase__sheet${
            darkMode ? ' tabs-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              {isVertical
                ? 'Vertical tabs beside content. Click a tab to switch panels.'
                : 'Horizontal segmented tabs above content. Click a tab to switch panels.'}
            </p>
            <div
              className={`tabs-showcase__panel${
                isVertical ? ' tabs-showcase__panel--vertical' : ''
              }${darkMode ? ' tabs-showcase__panel--dark' : ''}`}
            >
              <Tabs
                orientation={orientation}
                items={TABS_LIVE_ITEMS}
                value={activeTab}
                darkMode={darkMode}
                onChange={setActiveTab}
              />
              <div
                className={`tabs-showcase__content${
                  darkMode ? ' tabs-showcase__content--dark' : ''
                }`}
              >
                <h4 className="tabs-showcase__content-title">{activeLabel}</h4>
                <p className="tabs-showcase__content-body">
                  Content for the selected tab appears here.
                </p>
              </div>
            </div>
          </section>

          <section className="tabs-showcase__matrix">
            <h4 className="tabs-showcase__section-title">Tab counts</h4>
            {TABS_PREVIEW_COUNTS.map((count) => (
              <div
                key={count}
                className={`tabs-showcase__row${
                  count === highlightedCount
                    ? ' tabs-showcase__row--selected'
                    : ''
                }${darkMode ? ' tabs-showcase__row--dark' : ''}`}
              >
                <span className="tabs-showcase__row-label">
                  {TABS_PREVIEW_COUNT_LABELS[count]}
                </span>
                <Tabs
                  orientation={orientation}
                  items={TABS_DEMO_ITEMS.slice(0, count)}
                  previewActiveIndex={0}
                  darkMode={darkMode}
                />
              </div>
            ))}
          </section>

          <section className="tabs-showcase__matrix">
            <h4 className="tabs-showcase__section-title">Tab states</h4>
            <div className="tabs-showcase__states">
              <div
                className={`tabs-showcase__state-row${
                  darkMode ? ' tabs-showcase__state-row--dark' : ''
                }`}
              >
                <span className="tabs-showcase__row-label">Selected</span>
                <Tabs
                  orientation={orientation}
                  items={[{ id: 'selected', label: 'Tab' }]}
                  previewTabState="selected"
                  darkMode={darkMode}
                />
              </div>
              <div
                className={`tabs-showcase__state-row${
                  darkMode ? ' tabs-showcase__state-row--dark' : ''
                }`}
              >
                <span className="tabs-showcase__row-label">Default</span>
                <Tabs
                  orientation={orientation}
                  items={[{ id: 'default', label: 'Tab' }]}
                  previewTabState="default"
                  darkMode={darkMode}
                />
              </div>
            </div>
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel componentName="Tabs" sections={propertySections} />
    </>
  )
}

export default TabsShowcase
